/** XeAgents.ai **/
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { CheckCircle2, Circle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CallbackQueue from "@/components/healthcare/CallbackQueue";
import CallHandlingPanel from "@/components/healthcare/CallHandlingPanel";
import ClinicPanel, { type HealthcareSettings } from "@/components/healthcare/ClinicPanel";
import ConnectorPanel from "@/components/healthcare/ConnectorPanel";
import ReceptionistPanel from "@/components/healthcare/ReceptionistPanel";
import RegistrationFieldsPanel from "@/components/healthcare/RegistrationFieldsPanel";

interface CallbackLite {
  id: string;
}

function Step({ done, label, onClick }: { done: boolean; label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="flex items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-muted">
      {done ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
      <span className={done ? "text-muted-foreground" : "font-medium"}>{label}</span>
    </button>
  );
}

export default function Healthcare() {
  const { data, isLoading } = useQuery<{ settings: HealthcareSettings | null }>({ queryKey: ["/api/healthcare/settings"] });
  const settings = data?.settings ?? null;
  const { data: open = [] } = useQuery<CallbackLite[]>({ queryKey: ["/api/healthcare/callbacks"], enabled: !!settings, refetchInterval: 30_000 });
  const [tab, setTab] = useState<string | null>(null);

  const hasClinic = !!settings?.clinicName;
  // "Connected" means a choice was saved, not that the clinic software has been proven: the test button does that.
  const hasIntegration = !!settings && (settings.connectorType === "internal" || !!settings.connectorConfig?.baseUrl || !!settings.connectorConfig?.shard);
  const hasReceptionist = (settings?.agentIds.length ?? 0) > 0;
  const active = tab ?? (!hasClinic ? "clinic" : !hasReceptionist ? "integration" : "callbacks");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Healthcare front desk</h1>
        <p className="mt-1 text-muted-foreground">An AI receptionist for inbound calls: appointment booking, rescheduling and cancelling, routine questions, and hand-over to your team.</p>
      </div>

      {!isLoading && !(hasClinic && hasIntegration && hasReceptionist) && (
        <div className="rounded-lg border bg-muted/30 p-4">
          <p className="mb-2 text-sm font-medium">Set up in three steps</p>
          <div className="flex flex-wrap gap-1">
            <Step done={hasClinic} label="1. Clinic details" onClick={() => setTab("clinic")} />
            <Step done={hasIntegration && hasClinic} label="2. Integration: connect your software" onClick={() => setTab("integration")} />
            <Step done={hasReceptionist} label="3. Create the receptionist" onClick={() => setTab("receptionist")} />
          </div>
        </div>
      )}

      <Tabs value={active} onValueChange={setTab}>
        <TabsList className="flex h-auto flex-wrap justify-start">
          <TabsTrigger value="clinic" data-testid="tab-clinic">
            1. Clinic details
          </TabsTrigger>
          <TabsTrigger value="integration" data-testid="tab-integration">
            2. Integration
          </TabsTrigger>
          <TabsTrigger value="receptionist" data-testid="tab-receptionist">
            3. Receptionist
          </TabsTrigger>
          <TabsTrigger value="callbacks" data-testid="tab-callbacks">
            4. Callback queue
            {open.length > 0 && <Badge className="ml-2">{open.length}</Badge>}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="clinic" className="mt-4 space-y-6">
          <ClinicPanel settings={settings} />
          <CallHandlingPanel settings={settings} />
          <RegistrationFieldsPanel settings={settings} />
        </TabsContent>
        <TabsContent value="integration" className="mt-4">
          {settings ? <ConnectorPanel settings={settings} /> : <p className="text-sm text-muted-foreground">Save your clinic details first.</p>}
        </TabsContent>
        <TabsContent value="receptionist" className="mt-4">
          <ReceptionistPanel settings={settings} />
        </TabsContent>
        <TabsContent value="callbacks" className="mt-4">
          <CallbackQueue />
        </TabsContent>
      </Tabs>
    </div>
  );
}
